<template>
	<!-- UApp hosts Nuxt UI overlay/toast/tooltip portals; it must wrap the whole
	     shell so UPopover/UModal (currently only layers/changelog) can teleport. -->
	<UApp>
		<NuxtLayout>
			<main>
				<NuxtPage />
				<UiSidePanelManager />
			</main>
		</NuxtLayout>
		<!-- DevOnly, not the component's own v-if: a runtime guard still ships the
		     whole switcher (and its registry import) to production. -->
		
	</UApp>
</template>

<script setup lang="ts">
import { useDefaultsStore } from './stores/useDefaultsStore';
import { useLocationStore } from './stores/useLocationStore';

const theme = useLSTheme();

// The tenant resolves synchronously off the emitted registry, so `config` is
// populated for the page-agnostic side-effects gated on it below.
const { config, portalType } = useCustomerConfig();

// Parked "Coming soon" portals are a single static page with no map or booking
// UI, so don't pull in the Google Maps SDK for them.
const isParked = computed(() => !!config.value?.comingSoon);
if (!isParked.value) {
	useGoogleMaps();
}

const skipDefaults = computed(
	() => portalType.value === 'mapping' || portalType.value === 'supplier',
);

// Saved locations exist to feed the booking form's waypoint pickers, and support
// portals create jobs through SupportEditorPanel, never JobCreatePanel — so they
// have no consumer for this data. Deliberately NOT gated on the `locations` page
// flag: uat_cp_tow and uat_cp_limo disable that page while still running the
// booking form, and they need the picker's Favourites section.
const skipLocations = computed(
	() => skipDefaults.value || portalType.value === 'support',
);

const defaults = useDefaultsStore();
// Same gate as `locations.refresh()` below: support portals refresh defaults
// (for the customer prefill), and those defaults carry localities, which would
// otherwise drive useCombinedLocations' watch into fetching favourite locations
// they never consume. Passed as a getter so the gate tracks portalType if the
// customer config resolves after this setup runs.
const combinedLocations = useCombinedLocations({ autoFetch: () => !skipLocations.value });
// Saved locations feed the booking form's waypoint pickers through the shared
// `lcp-combined-locations` state. Fetched here at the app boundary, on login and
// alongside defaults, rather than inside JobCreatePanel: the panel is a consumer
// of this data, not its owner, and it must not reach into another domain's store.
const locations = useLocationStore({ immediate: false });

const { fetchChildAccounts, selectedAccount } = useAuth();
const authState = useState<{ isLoggedIn: boolean; userID?: string | null }>('lcp-auth');

const loggedIn = computed(() => authState.value?.isLoggedIn ?? false);

watch(
	loggedIn,
	(isLoggedIn, oldIsLoggedIn) => {
		// Enrich the ambient root logger with session identity so every subsequent
		// log is attributable to a user session (added late; pre-login logs simply
		// lack it). Emitted in plaintext (frogger.options.ts does NOT scrub `user`)
		// so logs can be attributed to a real account; the value is the account
		// username, never a typed secret.
		frogger.addContext({
			user: isLoggedIn ? (authState.value?.userID ?? null) : null,
		});

		if (isLoggedIn && !oldIsLoggedIn) {
			if (!skipDefaults.value) {
				defaults.refresh();
			}
			if (!skipLocations.value) {
				locations.refresh();
			}
			// Skip if child accounts were already loaded during SSR
			if (!selectedAccount.value) {
				fetchChildAccounts();
			}
		}
	},
	{ immediate: true },
);
</script>
